import type { ReactElement } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import BidVersionPtusDataGrid from '@/features/bidding/components/BidVersionPtusDataGrid'
import ActivationGroupSelector from '@/features/bidding/components/bidView/ActivationGroupSelector'
import BidUpdateVersionSelector from '@/features/bidding/components/bidView/BidUpdateVersionSelector'
import type { BidType } from '@/features/bidding/constants'
import { BidUpdateStatus, Status } from '@/features/bidding/constants'
import { useBidUpdatesQuery } from '@/features/bidding/hooks/useBidUpdatesQuery'
import type { ActivationGroupOption } from '@/features/bidding/types/activationGroup'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidUpdate, BidUpdateOverviewItem } from '@/features/bidding/types/bidUpdate'
import { getLatestBidUpdate } from '@/features/bidding/utils/bidUpdateTime'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'

type Props = {
  overview: BidUpdateOverviewItem[]
  groupParams: GroupedBidParams
  bidType: BidType
  actionComponent?: ReactElement
}

export const BidUpdatesView = ({ overview, groupParams, bidType, actionComponent }: Props) => {
  const { t } = useTranslation()

  const activationGroupOptions: ActivationGroupOption[] = useMemo(
    () =>
      overview
        .map((item) => ({ uuid: item.activationGroupUuid, code: item.activationGroupCode ?? item.activationGroupUuid }))
        .toSorted((ag1, ag2) => ag1.code.localeCompare(ag2.code) || ag1.uuid.localeCompare(ag2.uuid)),
    [overview],
  )

  const [selectedActivationGroup, setSelectedActivationGroup] = useState<ActivationGroupOption>(
    activationGroupOptions[0],
  )

  const { bidUpdates, isLoading } = useBidUpdatesQuery({
    activationGroupUuid: selectedActivationGroup.uuid,
    deliveryDay: groupParams.deliveryDay,
    bidType,
  })

  // Default to the latest update whenever the fetched updates change (group switch or initial load).
  const [selectedBidUpdate, setSelectedBidUpdate] = useState<BidUpdate | undefined>(undefined)
  useEffect(() => {
    setSelectedBidUpdate(bidUpdates && bidUpdates.length > 0 ? getLatestBidUpdate(bidUpdates) : undefined)
  }, [bidUpdates])

  if (bidUpdates && bidUpdates.length === 0) {
    return <p>{t('bidding.results.no_bids')}</p>
  }

  if (isLoading || !bidUpdates || !selectedBidUpdate) {
    return <FullPageSpinner />
  }

  return (
    <BidVersionPtusDataGrid
      actionComponent={actionComponent}
      activationGroupSelectorComponent={
        <ActivationGroupSelector
          activationGroups={activationGroupOptions}
          selectedActivationGroup={selectedActivationGroup}
          setSelectedActivationGroup={setSelectedActivationGroup}
        />
      }
      bidSelectorComponent={
        <BidUpdateVersionSelector
          bidUpdates={bidUpdates}
          selectedBidUpdate={selectedBidUpdate}
          setSelectedBidUpdate={setSelectedBidUpdate}
        />
      }
      bidVersion={toExtendedBid(selectedBidUpdate)}
      createdBy={selectedBidUpdate.createdBy}
      isLoading={false}
    />
  )
}

const toExtendedBid = (bidUpdate: BidUpdate): ExtendedBid => ({
  portfolio: bidUpdate.portfolio,
  deliveryDay: bidUpdate.deliveryDay,
  marketProgram: bidUpdate.marketProgram,
  activationGroupUuid: bidUpdate.activationGroupUuid,
  bidType: bidUpdate.bidType,
  status: bidUpdateStatusToStatus(bidUpdate.status),
  createdAt: bidUpdate.offeredTime ?? bidUpdate.acceptedTime ?? bidUpdate.deliveryDay.getStartOfDay(),
  ptus: bidUpdate.ptus,
})

/**
 * The PTU grid and its toolbar are shared with the regular bidding flow and are typed on
 * ExtendedBid / Status, so we adapt the selected bid update to that shape at this boundary.
 * The mapping below is only used to render the toolbar status chip — bid updates have no
 * acceptance/buyback concept, so acknowledgement maps to ACCEPTED and rejection/failure to REJECTED.
 */
const bidUpdateStatusToStatus = (status: BidUpdateStatus): Status => {
  switch (status) {
    case BidUpdateStatus.ACKNOWLEDGED:
      return Status.ACCEPTED
    case BidUpdateStatus.REJECTED:
    case BidUpdateStatus.FAILED:
      return Status.REJECTED
    case BidUpdateStatus.PENDING:
    default:
      return Status.OFFERED
  }
}
